import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import ArrowForward from "@mui/icons-material/ArrowForward";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Skeleton, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FloatingSaveBar } from "components/FloatingSaveBar";
import { IssueCount } from "components/IssueCount";
import { CatalogLink } from "components/ModelLinks/CatalogLink";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import { FacilityProductMeasurementsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useCallback, useMemo, useState } from "react";
import { prettyPrintMeasurementRange } from "util/dates";

import { FacilityProductMeasurementForm } from "./FacilityProductMeasurementForm";
import { FACILITY_PRODUCT_MEASUREMENT } from "./Inventory/Facility/schema";

type Row = FacilityProductMeasurementsQuery["facilityProductMeasurements"]["items"][0];

const PRODUCT_FOR_FACILITY = gql(/** GraphQL */ `
  query ProductForFacility($productId: ID!, $facilityId: ID!) {
    productForFacility(productId: $productId, facilityId: $facilityId) {
      id
      name
      alternateId
      productChemicals {
        chemical {
          id
          name
        }
      }
      issues {
        ...issue
      }
    }
  }
`);

const DELETE_FACILITY_PRODUCT_MEASUREMENT = gql(/** GraphQL */ `
  mutation DeleteFacilityProductMeasurement($id: ID!) {
    deleteFacilityProductMeasurement(id: $id) {
      id
    }
  }
`);

export function FacilityProductMeasurementTable({
  facilityId,
  showReportingYearPicker,
}: {
  facilityId: string;
  showReportingYearPicker?: boolean;
}) {
  const alerts = useAlerts();
  const [editingMeasurementId, setEditingMeasurementId] = useState<string | undefined>();
  const [measurementSeed, setMeasurementSeed] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deletingMeasurement, setDeletingMeasurement] = useState<Row | undefined>();

  const reportingYear = useSelectedReportingYear();
  const { popIdFromUrl, selectedId: productId } = useSelectedIdInUrl("productId");

  const { data: productData, loading } = useQuery(PRODUCT_FOR_FACILITY, {
    variables: {
      productId: productId ?? "",
      facilityId,
    },
    skip: !productId,
  });

  const [deleteMeasurement, { loading: isDeleting }] = useMutation(
    DELETE_FACILITY_PRODUCT_MEASUREMENT,
    {
      onCompleted() {
        alerts.success("Successfully deleted the measurement");
        setDeletingMeasurement(undefined);
      },
      onError(err) {
        alerts.error("An error occurred deleting the measurement", err);
        setDeletingMeasurement(undefined);
      },
      refetchQueries: [FACILITY_PRODUCT_MEASUREMENT],
    }
  );

  const handleDelete = useCallback(() => {
    if (deletingMeasurement) {
      deleteMeasurement({
        variables: {
          id: deletingMeasurement.id,
        },
      });
    }
  }, [deleteMeasurement, deletingMeasurement]);

  const columns = useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "location",
        valueFormatter: (_, row: Row) => row.storageLocation?.description,
        headerName: "Storage Location",
        flex: 1,
      },
      {
        field: "measuredAtUtc",
        headerName: "Measurement Date",
        valueFormatter: (_, row: Row) => {
          const measurementYear = row.measuredAtUtc.slice(0, 4);
          const display = prettyPrintMeasurementRange(row.measuredAtUtc, row.endDate);
          return `${display}${
            parseInt(measurementYear, 10) < reportingYear ? " (carry-over)" : ""
          }`;
        },
        flex: 1,
      },
      {
        field: "quantity",
        headerName: "Quantity on Hand",
        filterKeyType: "number",
        flex: 1,
        valueFormatter: (_, row: Row) =>
          prettyPrintQuantity({
            amount: row.quantity,
            includeUnit: false,
          }),
      },
      {
        field: "issues",
        headerName: "Issues",
        align: "center",
        headerAlign: "center",
        flex: 0.5,
        renderCell: ({ row }) => {
          return <IssueCount issueCount={row.issues.length} />;
        },
      },
      {
        field: "actions",
        type: "actions",
        width: 120,
        getActions: (params) => [
          <GridActionsCellItem
            onClick={() => {
              setEditingMeasurementId(params.row.id);
            }}
            key={1}
            label="Edit"
            icon={<Edit />}
          />,
          <GridActionsCellItem
            onClick={() => setDeletingMeasurement(params.row)}
            key={2}
            label="Delete Measurement"
            icon={<Delete />}
          />,
          <GridActionsCellItem
            onClick={() => {
              setMeasurementSeed(params.row.id);
            }}
            key={3}
            label="Quick Add"
            icon={<Add />}
          />,
        ],
      },
    ],
    [reportingYear]
  );

  if (loading) {
    return (
      <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    );
  }

  return (
    <>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Daily Measurements for {productData?.productForFacility.name}
        {productData?.productForFacility.alternateId?.trim()?.length
          ? ` (${productData?.productForFacility.alternateId})`
          : ""}
      </Typography>
      <OmnisearchDataGrid
        persistenceKey="facility-product-measurements"
        isURLDriven
        columns={columns}
        dataQuery={FACILITY_PRODUCT_MEASUREMENT}
        getCount={(d) => d.facilityProductMeasurements.count}
        getItems={(d) => d.facilityProductMeasurements.items ?? []}
        defaultSearch={`productId:${productId} facilityId:${facilityId} reportingYear:${reportingYear}`}
        initialSortModel={[
          { field: "location", sort: "desc" },
          { field: "measuredAtUtc", sort: "desc" },
        ]}
        commandButtons={[
          showReportingYearPicker ? (
            <TierIIYearPicker
              key="yearPicker"
              selectProps={{
                size: "small",
              }}
              persistToLocalStorage
            />
          ) : undefined,
          <CommandButton key="add" variant="contained" onClick={() => setAddDialogOpen(true)}>
            Add Measurement
          </CommandButton>,
        ]}
        onRowClick={(params) => {
          setEditingMeasurementId(params.row.id);
        }}
        withPadding={false}
        skip={!productId || !reportingYear || !facilityId}
        excludeFilterColumns={["issues"]}
        noDataMessage={"No measurement information has been added to this facility yet."}
        noDataButtonText="Add Measurement"
        noDataOnButtonClick={() => setAddDialogOpen(true)}
      />
      <CatalogLink
        id={productData?.productForFacility.id ?? ""}
        type="Product"
        name={productData?.productForFacility.name ?? ""}
        issues={productData?.productForFacility.issues ?? []}
      />
      {(productData?.productForFacility?.productChemicals ?? []).map((pc, idx) => (
        <CatalogLink
          key={idx}
          id={pc.chemical.id ?? ""}
          type="Chemical"
          name={pc.chemical.name ?? ""}
          issues={productData?.productForFacility.issues ?? []}
        />
      ))}
      <FloatingSaveBar
        hideCancel={true}
        saveStartIcon={<ArrowForward />}
        saveText="Return to Products"
        onSaveClick={() => {
          popIdFromUrl();
        }}
      />
      <ConfirmDialog
        open={!!deletingMeasurement}
        onClose={() => setDeletingMeasurement(undefined)}
        onConfirm={handleDelete}
        header="Are you sure you want to delete this product measurement?"
        resources={[
          {
            resourceType: "Product Measurement",
            resourceName: productData?.productForFacility.name,
          },
        ]}
        loading={isDeleting}
      />
      {(editingMeasurementId || measurementSeed || addDialogOpen) && (
        <FacilityProductMeasurementForm
          open={!!editingMeasurementId || !!measurementSeed || addDialogOpen}
          facilityId={facilityId}
          measurementId={editingMeasurementId}
          product={productData?.productForFacility}
          measurementSeed={measurementSeed ?? undefined}
          onClose={() => {
            setEditingMeasurementId(undefined);
            setMeasurementSeed(null);
            setAddDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
